import React, { useCallback, useEffect, useState } from 'react';

import { Add, Edit, PanTool, Search } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import cx from 'clsx';
import { Form, Formik } from 'formik';
import { identity, isEqual } from 'lodash/fp';
import { theme } from 'muiTheme';
import { Link, useHistory } from 'react-router-dom';
import { usePrevious } from 'react-use';

import axios from '../modules/axios';
import { computeParams } from '../utils';
import DeleteWithConfirmation from './DeleteWithConfirmation';
import Resource from './Resource';

const useStyles = makeStyles({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  customRowStyle: props => (props?.customRowStyle ? props?.customRowStyle(theme) : {}),
});

const StyledTableRow = ({ children, customRowStyle }) => {
  const classes = useStyles({ customRowStyle });

  return <TableRow className={cx(classes.root, classes.customRowStyle)}>{children}</TableRow>;
};

export default ({
  resource,
  primaryKey,
  name,
  list,
  head,
  filter,
  associations,
  csv,
  sort,
  limit,
  filters,
  strictFilters = false,
  options,
  add,
  remove,
  edit,
  /* Function that allow to do stuff before loading rows. Results is forwarded to list function */
  prerequisitesProps,
  /* Provide a custom function top handle the specific update of your resource */
  customUpdate,
  /* Provide a custom function top handle the specific delete of your resource */
  customDelete,
  /* Funcion used to perfom actions on rows before displaying them (may include async actions) */
  transformRows = identity,
  show,
  addShow,
  popup,
  addData,
  query,
  select,
  customRowStyle,
  ...props
}) => {
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [_filters, setFilters] = useState({ ...filters });
  const previousFilters = usePrevious(_filters);
  const [count, setCount] = useState(0);
  const [pagination, setPagination] = useState({ sort: sort || null, limit: limit || 20, page: 1 });
  const getPages = () => Math.ceil(count / pagination.limit);

  const [editedResource, setEditedResource] = useState(null);

  const [selected, setSelected] = useState([]);
  const [preProps, setPreProps] = useState({});

  const updatePrerequisitesProps = useCallback(async () => {
    const res = await prerequisitesProps();

    setPreProps(res);
  }, [prerequisitesProps, setPreProps]);

  useEffect(() => {
    if (prerequisitesProps) {
      updatePrerequisitesProps();
    }
  }, [prerequisitesProps, updatePrerequisitesProps]);

  const loadRows = useCallback(async () => {
    // Recompute preprops before reloading rows
    if (prerequisitesProps) {
      await updatePrerequisitesProps();
    }

    const params = computeParams({ associations, query, pagination });

    for (let attr in _filters) {
      if (!(_filters[attr] === null || _filters[attr]?.length === 0)) {
        params.push(
          `${attr}=${encodeURIComponent(`${!strictFilters ? '%' : ''}${_filters[attr]}${!strictFilters ? '%' : ''}`)}`,
        );
      }
    }

    for (let attr in options) {
      if (!(options[attr] === null || options[attr]?.length === 0)) {
        params.push(`${attr}=${encodeURIComponent(`${options[attr]}`)}`);
      }
    }

    try {
      const result = await axios.get(`${resource}?${params.join('&')}`);

      setCount(result.headers['x-total-count']);
      if (pagination.limit !== result.headers['x-limit'] * 1)
        setPagination({ ...pagination, limit: result.headers['x-limit'] });

      const rawRows = result.data;

      const transformedRows = await transformRows(rawRows);

      setRows(transformedRows);
      setIsLoading(false);
    } catch (error) {
      console.error('Impossible de récupérer les données', error);
    }
  }, [
    prerequisitesProps,
    associations,
    query,
    pagination,
    updatePrerequisitesProps,
    _filters,
    strictFilters,
    options,
    resource,
    transformRows,
  ]);

  useEffect(() => {
    setIsLoading(true);
    setRows([]);
    setPagination({ sort: sort || null, limit: limit || 20, page: 1 });
    setFilters({ ...filters });
  }, [resource, filters, sort, limit]);

  useEffect(() => {
    const timer = setTimeout(() => {
      // Reset pagination to first page if filters have changed
      if (!isEqual(previousFilters, _filters)) {
        setPagination({ ...pagination, page: 1 });
      }

      loadRows();
    }, 500);

    return () => clearTimeout(timer);
  }, [_filters, previousFilters, query, pagination, resource, associations, loadRows]);

  const sortBy = attribute => {
    if (pagination.sort === attribute) return setPagination({ ...pagination, sort: `-${attribute}` });

    return setPagination({ ...pagination, sort: attribute });
  };

  const exportCSV = () => {
    let csvContent = rows.map(row => csv(row).join(',')).join('\n');
    downloadFile(csvContent);
  };

  const downloadFile = content => {
    let hiddenElement = document.createElement('a');
    hiddenElement.href = URL.createObjectURL(new Blob([content], { type: 'text/csv' }));
    hiddenElement.target = '_blank';
    hiddenElement.download = `${resource}.csv`;
    hiddenElement.click();
  };

  const addResource = () => {
    axios.post(resource, { ...addData, ...filters, ...options }).then(res => {
      loadRows();
      if (popup && !addShow) {
        setEditedResource(res.data[primaryKey]);
      } else if (show) {
        history.push(`/i9/${name}/${res.data[primaryKey]}`);
      }
    });
  };

  const removeResources = async rows => {
    setSelected([]);
    const requests = rows.map(async row => {
      if (customDelete) {
        await customDelete(row[primaryKey]);
      } else {
        await axios.delete(`${resource}/${row[primaryKey]}`);
      }
    });
    await Promise.all(requests);
    loadRows();
  };

  return (
    <React.Fragment>
      <Box p={1}>
        <Grid container direction='row' justifyContent='space-between' alignItems='baseline'>
          <Grid item xs={10}>
            {/* Resource filters */}
            <Formik initialValues={_filters} onSubmit={values => setFilters(values)}>
              {({ values, handleChange, handleSubmit, setFieldValue, setValues }) => (
                <Form onSubmit={handleSubmit} onChange={handleSubmit}>
                  <Grid container spacing={1} alignItems='baseline'>
                    {filter &&
                      filter({
                        values,
                        props: {
                          variant: 'outlined',
                          size: 'small',
                          onChange: handleChange,
                          fullWidth: true,
                          InputLabelProps: { shrink: true },
                        },
                        handleChange,
                        handleSubmit,
                        setFieldValue,
                        setValues,
                        filters,
                      }).map(
                        (f, index) =>
                          f && (
                            <Grid key={index} item xs={2}>
                              {f}
                            </Grid>
                          ),
                      )}
                  </Grid>
                </Form>
              )}
            </Formik>
            {/* End Resource filters */}
          </Grid>

          <Grid item container justifyContent='flex-end'>
            {select && selected.length > 0 && (
              <Box mr={1}>
                <Button variant='contained' color='primary' onClick={() => select(selected)}>
                  <PanTool />
                </Button>
              </Box>
            )}
            {add && selected.length === 0 && (
              <Box mr={1}>
                <Button variant='contained' color='primary' onClick={() => addResource()}>
                  <Add />
                </Button>
              </Box>
            )}
            {remove && selected.length > 0 && <DeleteWithConfirmation onRemove={() => removeResources(selected)} />}
            {csv && (
              <Box mr={1}>
                <Button variant='contained' onClick={exportCSV}>
                  Export
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      {isLoading ? (
        <Grid container justifyContent='center'>
          <Box my={2}>
            <CircularProgress />
          </Box>
        </Grid>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {remove && (
                    <TableCell>
                      {rows.length > 1 && (
                        <Checkbox
                          checked={selected.length === rows.length}
                          onChange={event => {
                            selected.length === 0 ? setSelected(rows) : setSelected([]);
                          }}
                        />
                      )}
                    </TableCell>
                  )}

                  {Object.keys(head).map(key => (
                    <TableCell key={key} onClick={() => sortBy(key)}>
                      <strong>{head[key]}</strong>
                    </TableCell>
                  ))}
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <StyledTableRow key={`${name}-${index}`} customRowStyle={customRowStyle && customRowStyle(row)}>
                    {remove && (
                      <TableCell>
                        <Checkbox
                          checked={selected.some(s => s[primaryKey] === row[primaryKey])}
                          onChange={event => {
                            let newSelected = [];
                            if (event.target.checked) {
                              newSelected = [...selected, row];
                            } else {
                              newSelected = selected.filter(s => s[primaryKey] !== row[primaryKey]);
                            }
                            setSelected(newSelected);
                          }}
                        />
                      </TableCell>
                    )}
                    {list({ values: row, preProps }).map((l, index) => (
                      <TableCell key={index}>{typeof l === 'function' ? l(row) : l}</TableCell>
                    ))}
                    <TableCell align='right'>
                      {popup && (
                        <Box mb={1}>
                          <React.Fragment>
                            <Button
                              variant='contained'
                              color='primary'
                              onClick={() => setEditedResource(row[primaryKey])}
                            >
                              <Edit />
                            </Button>{' '}
                          </React.Fragment>
                        </Box>
                      )}
                      {show && (
                        <React.Fragment>
                          <Link to={{ pathname: `/i9/${name}/${row[primaryKey]}` }}>
                            <Button variant='contained' color='primary'>
                              <Search />
                            </Button>
                          </Link>{' '}
                        </React.Fragment>
                      )}
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box p={1}>
            <Grid container direction='row' justifyContent='space-between' alignItems='baseline'>
              {getPages() > 1 && (
                <React.Fragment>
                  <Grid item>
                    <strong>{count} résultats</strong>
                  </Grid>
                  <Grid item>
                    <Pagination
                      count={getPages()}
                      page={pagination.page}
                      onChange={(e, value) => setPagination({ ...pagination, page: value })}
                    />
                  </Grid>
                </React.Fragment>
              )}
            </Grid>
          </Box>
        </>
      )}

      <Modal
        open={editedResource !== null}
        onClose={() => setEditedResource(null)}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            minWidth: 350,
            maxWidth: 800,
            maxHeight: 900,
            overflowY: 'auto',
          }}
        >
          <Resource
            {...props}
            name={name}
            resource={resource}
            resourceId={editedResource}
            associations={associations}
            query={query}
            edit={edit}
            preProps={preProps}
            customUpdate={customUpdate}
            customDelete={customDelete}
            remove={remove}
            onEdit={() => {
              loadRows();
              setEditedResource(null);
            }}
          />
        </Box>
      </Modal>
    </React.Fragment>
  );
};
